// Boot and lifetime of the session: read the token from the URL, exchange it, keep the result in memory, renew near expiry.
import { Theme, UiLang, type SsoExchangeResponse } from "@imagora/contracts";
import { z } from "zod";
import { ApiError } from "./api";
import type { HostBridge } from "./host-bridge";

export type Session = SsoExchangeResponse;

export type SessionState =
  | { status: "booting" }
  | { status: "ready"; session: Session }
  /** Opened directly, without a token and with no parent to ask. */
  | { status: "none" }
  | { status: "error"; code: string };

/** A person who signed in here rather than arriving from a host: the token outlives a reload, not the tab. */
export interface DirectSessionStorage {
  load(): string | null;
  save(token: string | null): void;
  restore(token: string): Promise<Session>;
}

/** What a push subscription leaves behind: a way in when a notice is tapped, for a person with no letter to hand and maybe no sign-in of their own. */
export interface DeviceSessionStorage {
  /** The token for this business; with none named, whatever the browser allows without one having been tapped. */
  load(tenantId: string | null): string | null;
  forget(token: string): void;
  open(token: string): Promise<Session>;
}

export interface SessionDeps {
  exchange: (token: string) => Promise<Session>;
  bridge: HostBridge;
  direct?: DirectSessionStorage;
  device?: DeviceSessionStorage;
  now?: () => number;
  /** How long the parent gets to answer imagora:need-token. */
  tokenTimeoutMs?: number;
  /** How long before expiry the parent is asked for a new token. */
  renewBeforeMs?: number;
  /** A floor, so a host handing out short sessions cannot put us in a tight loop. */
  minRenewDelayMs?: number;
  /** Opens another client of an agency, with the session just renewed. */
  reopen?: (token: string, tenantId: string) => Promise<Session>;
}

const DEFAULT_TOKEN_TIMEOUT_MS = 8000;
const DEFAULT_RENEW_BEFORE_MS = 5 * 60_000;
const DEFAULT_MIN_RENEW_DELAY_MS = 30_000;

/** A tapped notice says whose it is, for a phone signed in to more than one business. The address goes back to what the page is. */
export function takeWantedTenant(href: string): { tenant: string | null; cleanUrl: string | null } {
  const url = new URL(href);
  const tenant = url.searchParams.get("tenant");
  if (tenant === null) return { tenant: null, cleanUrl: null };
  url.searchParams.delete("tenant");
  return { tenant: tenant || null, cleanUrl: `${url.pathname}${url.search}${url.hash}` };
}

/** Finds `?sso=` or `#sso=` and returns the URL without it. The caller replaces the URL before anything else can read it. */
export function takeBootToken(href: string): { token: string | null; cleanUrl: string | null } {
  const url = new URL(href);
  const hash = new URLSearchParams(url.hash.startsWith("#") ? url.hash.slice(1) : url.hash);
  const fromQuery = url.searchParams.get("sso");
  const fromHash = hash.get("sso");
  if (!fromQuery && !fromHash) return { token: null, cleanUrl: null };
  url.searchParams.delete("sso");
  hash.delete("sso");
  url.hash = hash.toString();
  return { token: fromQuery ?? fromHash, cleanUrl: `${url.pathname}${url.search}${url.hash}` };
}

export function createSessionStore(deps: SessionDeps) {
  const now = deps.now ?? (() => Date.now());
  const tokenTimeoutMs = deps.tokenTimeoutMs ?? DEFAULT_TOKEN_TIMEOUT_MS;
  const renewBeforeMs = deps.renewBeforeMs ?? DEFAULT_RENEW_BEFORE_MS;
  const minRenewDelayMs = deps.minRenewDelayMs ?? DEFAULT_MIN_RENEW_DELAY_MS;

  let state: SessionState = { status: "booting" };
  let renewTimer: ReturnType<typeof setTimeout> | undefined;
  let refreshing: Promise<boolean> | null = null;
  let pendingToken: { resolve: (token: string | null) => void } | null = null;
  /** The client an agency person moved to inside a host's frame. The host only ever signs for the one it opened. */
  let openedTenantId: string | null = null;
  const listeners = new Set<() => void>();

  const setState = (next: SessionState) => {
    state = next;
    for (const listener of listeners) listener();
  };

  function scheduleRenewal(session: Session) {
    clearTimeout(renewTimer);
    const delay = new Date(session.expiresAt).getTime() - now() - renewBeforeMs;
    // Browsers fire a timer longer than 2^31 ms at once, and a session this long means a clock problem anyway.
    if (delay > 2 ** 31 - 1) return;
    renewTimer = setTimeout(() => void refresh("expiring"), Math.max(minRenewDelayMs, delay));
    (renewTimer as { unref?: () => void }).unref?.();
  }

  async function adopt(token: string): Promise<void> {
    const session = await deps.exchange(token);
    if (!deps.bridge.trust(session.allowedOrigins)) throw new ApiError("embed_origin_not_allowed", 403);
    scheduleRenewal(session);
    setState({ status: "ready", session });
  }

  /** A token renewing the session opens the client that was open. If that client is no longer theirs, they land where the host signed them in. */
  async function adoptRenewed(token: string): Promise<void> {
    const opened = openedTenantId;
    const session = await deps.exchange(token);
    if (!deps.bridge.trust(session.allowedOrigins)) throw new ApiError("embed_origin_not_allowed", 403);
    let next = session;
    if (opened && opened !== session.tenant.id && deps.reopen) {
      try {
        next = await deps.reopen(session.token, opened);
      } catch {
        openedTenantId = null;
      }
    }
    scheduleRenewal(next);
    setState({ status: "ready", session: next });
  }

  function askParent(reason: "boot" | "expiring" | "expired"): Promise<string | null> {
    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        pendingToken = null;
        resolve(null);
      }, tokenTimeoutMs);
      pendingToken = {
        resolve: (token) => {
          clearTimeout(timer);
          pendingToken = null;
          resolve(token);
        },
      };
      deps.bridge.askForToken(reason);
    });
  }

  deps.bridge.subscribe((message) => {
    if (message.type !== "imagora:token") return;
    if (pendingToken) pendingToken.resolve(message.token);
    else {
      // The host chose this one by itself, so it stands.
      openedTenantId = null;
      void adopt(message.token).catch(() => undefined);
    }
  });

  async function openDevice(tenantId: string | null): Promise<Session | null> {
    if (deps.bridge.embedded) return null;
    const token = deps.device?.load(tenantId);
    if (!token) return null;
    try {
      const session = await deps.device!.open(token);
      deps.direct?.save(session.token);
      return session;
    } catch (err) {
      // Only a clear no loses it. A phone with no signal keeps its way in for the next tap.
      if (err instanceof ApiError && err.status === 401) deps.device!.forget(token);
      return null;
    }
  }

  /** Asks the parent for a new token. Concurrent callers share one attempt. */
  function refresh(reason: "expiring" | "expired"): Promise<boolean> {
    if (!deps.bridge.embedded) {
      if (reason !== "expired") return Promise.resolve(false);
      refreshing ??= (async () => {
        try {
          const session = await openDevice(state.status === "ready" ? state.session.tenant.id : null);
          if (session) {
            setState({ status: "ready", session });
            return true;
          }
          // Signed in here: the way back is the sign-in page, which the screen without a session offers.
          if (deps.direct?.load()) {
            deps.direct.save(null);
            setState({ status: "none" });
          } else setState({ status: "error", code: "session_expired" });
          return false;
        } finally {
          refreshing = null;
        }
      })();
      return refreshing;
    }
    refreshing ??= (async () => {
      try {
        const token = await askParent(reason);
        if (token) {
          await adoptRenewed(token);
          return true;
        }
      } catch {
        // Falls through: the current session is still good until the API says otherwise.
      } finally {
        refreshing = null;
      }
      if (reason === "expired") setState({ status: "error", code: "session_expired" });
      return false;
    })();
    return refreshing;
  }

  return {
    getState: () => state,
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getToken: () => (state.status === "ready" ? state.session.token : null),
    /** Called once. A parent that opened us without a token gets asked for one, which also covers a reload of the iframe. */
    async boot(token: string | null, wantedTenant: string | null = null): Promise<void> {
      try {
        const first = token ?? (deps.bridge.embedded ? await askParent("boot") : null);
        if (!first) {
          const saved = deps.bridge.embedded ? null : deps.direct?.load();
          let restored: Session | null = null;
          if (saved) {
            try {
              restored = await deps.direct!.restore(saved);
            } catch {
              deps.direct!.save(null);
            }
          }
          // The tab's own session unless a notice for another business was tapped; then that business's way in, if this browser has one.
          const session = restored && (!wantedTenant || restored.tenant.id === wantedTenant) ? restored : ((await openDevice(wantedTenant)) ?? restored);
          setState(session ? { status: "ready", session } : { status: "none" });
          return;
        }
        await adopt(first);
      } catch (err) {
        setState({ status: "error", code: err instanceof ApiError ? err.code : "unknown" });
      }
    },
    refreshAfterUnauthorized: () => refresh("expired"),
    /** After a sign-in link or a change of business. Only when not embedded: a host owns its own frame's session. */
    adoptDirect(session: Session) {
      if (deps.bridge.embedded) return;
      deps.direct?.save(session.token);
      setState({ status: "ready", session });
    },
    /** An agency person opening another of its clients, inside a host's frame too. */
    adoptOpened(session: Session) {
      if (deps.bridge.embedded) {
        openedTenantId = session.tenant.id;
        scheduleRenewal(session);
        setState({ status: "ready", session });
        return;
      }
      deps.direct?.save(session.token);
      setState({ status: "ready", session });
    },
    signOut() {
      deps.direct?.save(null);
      clearTimeout(renewTimer);
      setState({ status: "none" });
    },
    /** Whether this session came from signing in here. */
    isDirect: () => !deps.bridge.embedded && Boolean(deps.direct?.load()),
    stop() {
      clearTimeout(renewTimer);
    },
  };
}

export type SessionStore = ReturnType<typeof createSessionStore>;

const TokenHints = z.object({ lang: UiLang.optional(), theme: Theme.optional() });

/**
 * Reads language and theme from the token without verifying it, only so the first paint matches the host while the
 * exchange runs. Nothing is decided from this: the verified values replace it when the exchange returns.
 */
export function peekTokenHints(token: string): z.output<typeof TokenHints> {
  try {
    const payload = token.split(".")[1] ?? "";
    const json = new TextDecoder().decode(Uint8Array.from(atob(payload.replace(/-/g, "+").replace(/_/g, "/")), (ch) => ch.charCodeAt(0)));
    const parsed = TokenHints.safeParse(JSON.parse(json));
    return parsed.success ? parsed.data : {};
  } catch {
    return {};
  }
}
